<template>
  <div
    v-if="showTimer && isRoleAllowed"
    class="floating-countdown-timer-container"
    @click="unlockAudio"
  >
    <!-- MINIMIZED STATE: Premium Compact Countdown Pill -->
    <div
      v-if="isMinimized"
      class="countdown-pill-mini d-flex align-center cursor-pointer"
      :class="{
        'urgent-pill': isUrgent && !isExpiredState,
        'final-pill': isFinalCount && !isExpiredState,
        'expired-pill': isExpiredState,
      }"
      @click="toggleMinimize"
    >
      <div class="pill-pulse-ring" />

      <!-- Timer Icon with Pulse -->
      <div class="pill-icon-wrap mr-2">
        <v-icon
          v-if="isExpiredState"
          color="#56BDA3"
          size="18"
        >
          mdi-check-decagram
        </v-icon>
        <v-icon
          v-else
          :color="isUrgent ? '#EDBE38' : '#0097D7'"
          size="18"
          class="ticking-timer-icon"
        >
          mdi-progress-clock
        </v-icon>
      </div>

      <!-- Compact Countdown Digits -->
      <div class="mini-countdown-text mr-2">
        <template v-if="isExpiredState">
          <span class="expired-mini-label">CLOSED</span>
        </template>
        <template v-else>
          <span
            v-if="days > 0"
            class="mini-days"
          >{{ formatTwoDigits(days) }}d
          </span>
          <span
            class="mini-time"
            :class="{ 'tick-bounce': isSecondTicking }"
          >
            {{ formatTwoDigits(hours) }}:{{ formatTwoDigits(minutes) }}:{{
              formatTwoDigits(seconds)
            }}
          </span>
        </template>
      </div>

      <!-- Sound Toggle Icon Button -->
      <v-tooltip top>
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            x-small
            dark
            class="mini-sound-btn mr-1"
            v-bind="attrs"
            v-on="on"
            @click.stop="toggleSound"
          >
            <v-icon
              size="14"
              :color="isSoundMuted ? '#FFFFFF' : '#56BDA3'"
            >
              {{ isSoundMuted ? "mdi-volume-off" : "mdi-volume-high" }}
            </v-icon>
          </v-btn>
        </template>
        <span>{{
          isSoundMuted ? "Enable Timer Sound" : "Mute Timer Sound"
        }}</span>
      </v-tooltip>

      <!-- Expand Button -->
      <v-btn
        icon
        x-small
        dark
        class="mini-expand-btn"
      >
        <v-icon size="16">
          mdi-chevron-up
        </v-icon>
      </v-btn>
    </div>

    <!-- EXPANDED STATE: Premium Floating Glassmorphism Countdown Card -->
    <div
      v-else
      class="countdown-card-expanded pa-4 rounded-xl"
      :class="{
        'urgent-card': isUrgent && !isExpiredState,
        'critical-card': isCritical && !isExpiredState,
        'final-card': isFinalCount && !isExpiredState,
        'expired-card': isExpiredState,
      }"
    >
      <!-- Ambient Glow & Border Sweep Effect -->
      <div class="border-glow-line" />
      <div class="ambient-radial-glow" />

      <!-- COMPLETED STATE DISPLAY -->
      <template v-if="isExpiredState">
        <div
          class="completion-container d-flex flex-column align-center justify-center text-center py-2"
        >
          <div class="d-flex align-center justify-space-between w-100 mb-3">
            <div class="d-flex align-center">
              <span class="live-dot expired-dot mr-2" />
              <span class="timer-eyebrow text-uppercase font-weight-bold">
                COUNTDOWN COMPLETE
              </span>
            </div>
            <v-btn
              icon
              x-small
              dark
              class="card-action-btn"
              @click="toggleMinimize"
            >
              <v-icon size="16">
                mdi-minus
              </v-icon>
            </v-btn>
          </div>

          <div class="completion-icon-wrapper mb-2">
            <v-icon
              color="#56BDA3"
              size="44"
              class="completion-check-icon"
            >
              mdi-check-decagram
            </v-icon>
          </div>
          <h4 class="completion-title">
            WITHDRAWAL CLOSED
          </h4>
          <span class="completion-subtitle">TIME EXPIRED</span>
          <span
            v-if="targetDateText"
            class="target-date-subtext mt-2"
          >
            Closed on {{ targetDateText }}
          </span>
        </div>
      </template>

      <!-- ACTIVE COUNTDOWN DISPLAY -->
      <template v-else>
        <!-- Card Header -->
        <div
          class="d-flex align-center justify-space-between mb-3 position-relative"
        >
          <div class="d-flex align-center">
            <div class="timer-icon-badge">
              <v-icon
                :color="isUrgent ? '#EDBE38' : '#0097D7'"
                size="18"
                class="ticking-timer-icon"
              >
                mdi-timer-outline
              </v-icon>
            </div>
            <div class="timer-header-text">
              <div class="timer-eyebrow d-flex align-center">
                <span
                  class="live-dot"
                  :class="{ 'urgent-dot': isUrgent }"
                />
                <span>LIVE COUNTDOWN</span>
              </div>
              <div class="timer-main-title">
                Withdrawal closes in
              </div>
            </div>
          </div>

          <!-- Top Action Controls -->
          <div class="d-flex align-center card-actions-group">
            <!-- Sound Toggle Button -->
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  x-small
                  dark
                  class="card-action-btn"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="toggleSound"
                >
                  <v-icon
                    size="14"
                    :color="isSoundMuted ? '#FFFFFF' : '#56BDA3'"
                  >
                    {{ isSoundMuted ? "mdi-volume-off" : "mdi-volume-high" }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{
                isSoundMuted ? "Enable Timer Sound" : "Mute Timer Sound"
              }}</span>
            </v-tooltip>

            <!-- Minimize Button -->
            <v-btn
              icon
              x-small
              dark
              class="card-action-btn"
              @click="toggleMinimize"
            >
              <v-icon size="14">
                mdi-minus
              </v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Timer Segment Boxes Grid -->
        <div
          class="d-flex align-center justify-center gap-2 my-2 position-relative"
        >
          <!-- DAYS Segment (If days > 0) -->
          <div
            v-if="days > 0"
            class="timer-segment-box"
          >
            <span class="segment-number">{{ formatTwoDigits(days) }}</span>
            <span class="segment-label">DAYS</span>
          </div>
          <span
            v-if="days > 0"
            class="segment-colon"
          >:</span>

          <!-- HOURS Segment -->
          <div class="timer-segment-box">
            <span class="segment-number">{{ formatTwoDigits(hours) }}</span>
            <span class="segment-label">HRS</span>
          </div>
          <span class="segment-colon">:</span>

          <!-- MINUTES Segment -->
          <div class="timer-segment-box">
            <span class="segment-number">{{ formatTwoDigits(minutes) }}</span>
            <span class="segment-label">MINS</span>
          </div>
          <span class="segment-colon">:</span>

          <!-- SECONDS Segment (Highlighted with Tick Animation & Glow) -->
          <div
            class="timer-segment-box seconds-segment"
            :class="{
              'seconds-ticking': isSecondTicking,
              'final-pulse': isFinalCount,
            }"
          >
            <span
              class="segment-number seconds-number"
              :class="{ 'tick-scale': isSecondTicking }"
            >
              {{ formatTwoDigits(seconds) }}
            </span>
            <span class="segment-label seconds-label">SECS</span>
          </div>
        </div>

        <!-- Target Date Subtitle -->
        <div
          v-if="targetDateText"
          class="target-date-subtext mt-3 text-center"
        >
          <v-icon
            size="12"
            class="mr-1"
            color="#56BDA3"
          >
            mdi-calendar-clock-outline
          </v-icon>
          <span>{{ targetDateText }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import companyConfig from "@/company_config.json";

export default {
  name: "WithdrawalTimer",
  props: {
    withdrawalDateTime: {
      type: [String, Date],
      default: null,
    },
    conference: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      fetchedConference: {},
      now: new Date(),
      timerInterval: null,
      fetchInterval: null,
      isMinimized: false,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalRemainingMs: 0,
      showTimer: false,

      // Enhanced UX & Sound State
      isSoundMuted:
        localStorage.getItem("withdrawal_timer_sound_muted") === "true",
      isSecondTicking: false,
      isExpiredState: false,
      audioCtx: null,
    };
  },
  computed: {
    isRoleAllowed() {
      let roleId = null;
      if (this.$session && typeof this.$session.get === "function") {
        roleId = this.$session.get("RoleId");
        if (!roleId) {
          const userObj = this.$session.get("user");
          if (userObj && (userObj.RoleId || userObj.role_id)) {
            roleId = userObj.RoleId || userObj.role_id;
          }
        }
      }
      if (!roleId && typeof localStorage !== "undefined") {
        roleId = localStorage.getItem("RoleId");
      }

      const numericRoleId = Number(roleId);
      const allowedRoles = [310, 311, 196];
      if (allowedRoles.includes(numericRoleId)) {
        return true;
      }

      let isEO = null;
      let isChairman = null;
      if (this.$session && typeof this.$session.get === "function") {
        isEO = this.$session.get("ElectionOfficerFlag");
        isChairman = this.$session.get(
          "NationalNominationCommitteeChairmanFlag"
        );
      }
      if (isEO === null && typeof localStorage !== "undefined") {
        isEO = localStorage.getItem("ElectionOfficerFlag");
      }
      if (isChairman === null && typeof localStorage !== "undefined") {
        isChairman = localStorage.getItem(
          "NationalNominationCommitteeChairmanFlag"
        );
      }

      if (isEO === true || isEO === 1 || isEO === "1" || isEO === "true")
        return true;
      if (
        isChairman === true ||
        isChairman === 1 ||
        isChairman === "1" ||
        isChairman === "true"
      )
        return true;

      return false;
    },
    activeConference() {
      if (
        this.conference &&
        (this.conference.WithdrawalDateTime ||
          this.conference.WithdrawalEndDate)
      ) {
        return this.conference;
      }
      return this.fetchedConference;
    },
    targetDateStr() {
      if (this.withdrawalDateTime) {
        return this.withdrawalDateTime;
      }
      const conf = this.activeConference;
      if (conf) {
        return (
          conf.WithdrawalDateTime ||
          conf.WithdrawalEndDate ||
          conf.conference_withdrawal_end_date ||
          ""
        );
      }
      return "";
    },
    targetDateText() {
      const conf = this.activeConference;
      if (conf && conf.WithdrawalEndDate) {
        return conf.WithdrawalEndDate;
      }
      if (typeof this.withdrawalDateTime === "string") {
        return this.withdrawalDateTime;
      }
      return "";
    },
    isUrgent() {
      return this.totalRemainingMs > 0 && this.totalRemainingMs < 3600000;
    },
    isCritical() {
      return this.totalRemainingMs > 0 && this.totalRemainingMs < 600000;
    },
    isFinalCount() {
      return this.totalRemainingMs > 0 && this.totalRemainingMs <= 10000;
    },
  },
  watch: {
    targetDateStr: {
      immediate: true,
      handler() {
        this.updateCountdown();
      },
    },
    $route() {
      this.handleRefreshTimer();
    },
    seconds(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.isSecondTicking = true;
        setTimeout(() => {
          this.isSecondTicking = false;
        }, 220);

        if (
          this.showTimer &&
          !this.isExpiredState &&
          this.totalRemainingMs > 0
        ) {
          this.playTickSound(this.isFinalCount);
        }
      }
    },
  },
  mounted() {
    this.fetchConferenceDetails();
    this.updateCountdown();
    this.timerInterval = setInterval(() => {
      this.now = new Date();
      this.updateCountdown();
    }, 1000);

    this.fetchInterval = setInterval(() => {
      this.fetchConferenceDetails();
    }, 60000);

    if (typeof window !== "undefined") {
      window.addEventListener(
        "refresh-withdrawal-timer",
        this.handleRefreshTimer
      );
    }
    if (this.$root && typeof this.$root.$on === "function") {
      this.$root.$on("refresh-withdrawal-timer", this.handleRefreshTimer);
    }
  },
  beforeUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener(
        "refresh-withdrawal-timer",
        this.handleRefreshTimer
      );
    }
    if (this.$root && typeof this.$root.$off === "function") {
      this.$root.$off("refresh-withdrawal-timer", this.handleRefreshTimer);
    }
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.fetchInterval) clearInterval(this.fetchInterval);
    if (this.audioCtx) {
      try {
        this.audioCtx.close();
      } catch (e) {
        // ignore
      }
    }
  },
  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener(
        "refresh-withdrawal-timer",
        this.handleRefreshTimer
      );
    }
    if (this.$root && typeof this.$root.$off === "function") {
      this.$root.$off("refresh-withdrawal-timer", this.handleRefreshTimer);
    }
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.fetchInterval) clearInterval(this.fetchInterval);
  },
  methods: {
    handleRefreshTimer() {
      this.fetchedConference = {};
      this.fetchConferenceDetails(true);
    },
    fetchConferenceDetails(force = false) {
      if (!this.isRoleAllowed) {
        this.showTimer = false;
        return;
      }
      if (
        !force &&
        (this.withdrawalDateTime ||
          (this.conference &&
            (this.conference.WithdrawalDateTime ||
              this.conference.WithdrawalEndDate)))
      ) {
        return;
      }
      try {
        var token =
          (this.$session ? this.$session.get("token") : null) ||
          (typeof localStorage !== "undefined"
            ? localStorage.getItem("token")
            : null);
        if (!token) return;
        var server_url = companyConfig.apiURL;
        var add_url = server_url + "api/nomination-conference/show";
        var password =
          (this.$session ? this.$session.get("Password") : null) ||
          (typeof localStorage !== "undefined"
            ? localStorage.getItem("Password")
            : "") ||
          "";
        var upload = {
          UserInterface: 2,
          Password: password,
        };
        const thisIns = this;
        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            if (
              response.data &&
              response.data.flag == 1 &&
              response.data.records
            ) {
              thisIns.fetchedConference = response.data.records;
              thisIns.updateCountdown();
            }
          })
          .catch(function (error) {
            console.log("WithdrawalTimer fetch error:", error);
          });
      } catch (e) {
        console.log("WithdrawalTimer error:", e);
      }
    },
    parseDateTime(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return null;
      const str = dateStr.trim();
      if (
        !str ||
        str === "null" ||
        str === "undefined" ||
        str.startsWith("0000")
      ) {
        return null;
      }

      if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}/.test(str)) {
        const iso = str.replace(" ", "T");
        const parsed = new Date(iso);
        if (!isNaN(parsed.getTime())) return parsed;
      }

      const parsed = new Date(str);
      if (!isNaN(parsed.getTime())) return parsed;

      return null;
    },
    updateCountdown() {
      if (!this.isRoleAllowed) {
        this.showTimer = false;
        this.totalRemainingMs = 0;
        this.isExpiredState = false;
        return;
      }
      const target = this.parseDateTime(this.targetDateStr);
      if (!target) {
        this.showTimer = false;
        this.totalRemainingMs = 0;
        this.isExpiredState = false;
        return;
      }

      const diff = target.getTime() - this.now.getTime();
      if (diff <= 0) {
        if (this.showTimer && !this.isExpiredState) {
          this.playCompletionSound();
        }
        this.showTimer = true;
        this.isExpiredState = true;
        this.totalRemainingMs = 0;
        this.days = 0;
        this.hours = 0;
        this.minutes = 0;
        this.seconds = 0;
        return;
      }

      this.showTimer = true;
      this.isExpiredState = false;
      this.totalRemainingMs = diff;

      this.days = Math.floor(diff / (1000 * 60 * 60 * 24));
      this.hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      this.minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      this.seconds = Math.floor((diff % (1000 * 60)) / 1000);
    },
    formatTwoDigits(num) {
      return String(num).padStart(2, "0");
    },
    toggleMinimize() {
      this.unlockAudio();
      this.isMinimized = !this.isMinimized;
    },
    toggleSound() {
      this.unlockAudio();
      this.isSoundMuted = !this.isSoundMuted;
      localStorage.setItem("withdrawal_timer_sound_muted", this.isSoundMuted);
    },
    unlockAudio() {
      if (!this.audioCtx) {
        const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
        if (AudioCtxClass) {
          this.audioCtx = new AudioCtxClass();
        }
      }
      if (this.audioCtx && this.audioCtx.state === "suspended") {
        this.audioCtx.resume();
      }
    },
    playTickSound(isFinal = false) {
      if (this.isSoundMuted) return;
      this.unlockAudio();
      if (!this.audioCtx || this.audioCtx.state !== "running") return;

      try {
        const now = this.audioCtx.currentTime;
        const osc = this.audioCtx.createOscillator();
        const gain = this.audioCtx.createGain();

        osc.type = "sine";
        const freq = isFinal ? 1150 : 750;
        osc.frequency.setValueAtTime(freq, now);
        osc.frequency.exponentialRampToValueAtTime(freq / 2, now + 0.045);

        const volume = isFinal ? 0.12 : 0.06;
        gain.gain.setValueAtTime(volume, now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.045);

        osc.connect(gain);
        gain.connect(this.audioCtx.destination);

        osc.start(now);
        osc.stop(now + 0.045);
      } catch (e) {
        // ignore audio API errors
      }
    },
    playCompletionSound() {
      if (this.isSoundMuted) return;
      this.unlockAudio();
      if (!this.audioCtx || this.audioCtx.state !== "running") return;

      try {
        const now = this.audioCtx.currentTime;
        const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6 chime
        notes.forEach((freq, idx) => {
          const osc = this.audioCtx.createOscillator();
          const gain = this.audioCtx.createGain();
          osc.type = "triangle";
          osc.frequency.setValueAtTime(freq, now + idx * 0.09);

          gain.gain.setValueAtTime(0.12, now + idx * 0.09);
          gain.gain.exponentialRampToValueAtTime(
            0.0001,
            now + idx * 0.09 + 0.32
          );

          osc.connect(gain);
          gain.connect(this.audioCtx.destination);

          osc.start(now + idx * 0.09);
          osc.stop(now + idx * 0.09 + 0.32);
        });
      } catch (e) {
        // ignore audio API errors
      }
    },
  },
};
</script>

<style scoped>
/* ==========================================================================
   FLOATING COUNTDOWN TIMER CONTAINER
   ========================================================================== */
.floating-countdown-timer-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9995;
  font-family: "Outfit", "Inter", "Roboto", -apple-system, BlinkMacSystemFont,
    sans-serif;
  user-select: none;
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* ==========================================================================
   MINIMIZED STATE: COMPACT GLASS PILL
   ========================================================================== */
.countdown-pill-mini {
  background: linear-gradient(
    135deg,
    rgba(10, 15, 41, 0.94) 0%,
    rgba(30, 58, 112, 0.96) 100%
  );
  border: 1px solid #0097d7;
  box-shadow: 0 10px 30px rgba(10, 15, 41, 0.5),
    0 0 18px rgba(0, 151, 215, 0.25);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-radius: 40px;
  padding: 6px 14px;
  color: #ffffff;
  position: relative;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.countdown-pill-mini:hover {
  transform: translateY(-3px) scale(1.03);
  border-color: #0097d7;
  box-shadow: 0 14px 35px rgba(10, 15, 41, 0.6),
    0 0 25px rgba(0, 151, 215, 0.35);
}

.pill-pulse-ring {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 40px;
  pointer-events: none;
  animation: pill-glow-pulse 3s infinite ease-in-out;
}

@keyframes pill-glow-pulse {
  0%,
  100% {
    box-shadow: inset 0 0 10px rgba(0, 151, 215, 0.15);
  }
  50% {
    box-shadow: inset 0 0 18px rgba(0, 151, 215, 0.35);
  }
}

.pill-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.ticking-timer-icon {
  animation: subtle-spin 4s ease-in-out infinite;
}

@keyframes subtle-spin {
  0%,
  100% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(12deg);
  }
}

.mini-countdown-text {
  font-family: "JetBrains Mono", "Fira Code", monospace;
  font-weight: 800;
  font-size: 0.88rem;
  letter-spacing: -0.2px;
  color: #ffffff;
}

.mini-days {
  color: #0097d7;
  margin-right: 3px;
}

.mini-time {
  transition: transform 0.15s ease;
  display: inline-block;
  color: #ffffff;
}

.expired-mini-label {
  color: #56bda3;
  font-size: 0.82rem;
  letter-spacing: 0.5px;
}

.mini-sound-btn,
.mini-expand-btn {
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.14) !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: rgba(255, 255, 255, 0.22) !important;
  }
}

/* Minimized Urgency Modes */
.urgent-pill {
  border-color: #edbe38 !important;
  box-shadow: 0 10px 30px rgba(10, 15, 41, 0.5),
    0 0 20px rgba(237, 190, 56, 0.3) !important;
}

.final-pill {
  border-color: #edbe38 !important;
  animation: final-pill-pulse 1s infinite alternate;
}

@keyframes final-pill-pulse {
  0% {
    box-shadow: 0 0 10px rgba(237, 190, 56, 0.4);
  }
  100% {
    box-shadow: 0 0 22px rgba(237, 190, 56, 0.8);
  }
}

.expired-pill {
  border-color: #56bda3 !important;
}

/* ==========================================================================
   EXPANDED STATE: FUTURISTIC GLASS COUNTDOWN CARD
   ========================================================================== */
.countdown-card-expanded {
  background: linear-gradient(
    135deg,
    rgba(10, 15, 41, 0.96) 0%,
    rgba(30, 58, 112, 0.98) 100%
  );
  border: 1px solid rgba(0, 151, 215, 0.4);
  box-shadow: 0 20px 45px rgba(10, 15, 41, 0.65),
    0 0 30px rgba(0, 151, 215, 0.22);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  min-width: 310px;
  max-width: 350px;
  color: #ffffff;
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.border-glow-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    #0097d7 50%,
    transparent 100%
  );
  opacity: 0.8;
}

.ambient-radial-glow {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 140px;
  height: 140px;
  background: radial-gradient(
    circle,
    rgba(0, 151, 215, 0.22) 0%,
    transparent 70%
  );
  pointer-events: none;
}

/* Header Styling */
.timer-icon-badge {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: rgba(58, 103, 177, 0.2);
  border: 1px solid rgba(0, 151, 215, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 10px;
  box-shadow: 0 4px 12px rgba(0, 151, 215, 0.2);
}

.timer-header-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.timer-eyebrow {
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 1px;
  color: #0097d7;
  line-height: 1;
  margin-bottom: 3px;
  white-space: nowrap;
}

.live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #0097d7;
  box-shadow: 0 0 8px #0097d7;
  display: inline-block;
  flex-shrink: 0;
  margin-right: 6px;
  animation: live-dot-pulse 1.8s infinite ease-in-out;
}

.urgent-dot {
  background: #edbe38 !important;
  box-shadow: 0 0 8px #edbe38 !important;
}

.expired-dot {
  background: #56bda3 !important;
  box-shadow: 0 0 8px #56bda3 !important;
}

@keyframes live-dot-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.4);
    opacity: 0.5;
  }
}

.timer-main-title {
  font-size: 0.86rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
  white-space: nowrap;
}

.card-actions-group {
  gap: 6px;
  flex-shrink: 0;
  margin-left: 8px;
}

.card-action-btn {
  width: 26px !important;
  height: 26px !important;
  min-width: 26px !important;
  border-radius: 50% !important;
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.14) !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: rgba(255, 255, 255, 0.22) !important;
    transform: translateY(-1px);
  }
}

/* Segment Boxes Styling */
.timer-segment-box {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(58, 103, 177, 0.35);
  border-radius: 14px;
  padding: 8px 10px;
  min-width: 52px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.15),
    0 4px 14px rgba(10, 15, 41, 0.3);
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.segment-number {
  font-family: "JetBrains Mono", "Fira Code", monospace;
  font-size: 1.45rem;
  font-weight: 900;
  color: #ffffff;
  line-height: 1;
  text-shadow: 0 0 12px rgba(0, 151, 215, 0.5);
}

.segment-label {
  font-size: 0.58rem;
  font-weight: 800;
  color: #0097d7;
  letter-spacing: 0.8px;
  margin-top: 4px;
}

.segment-colon {
  font-family: "JetBrains Mono", monospace;
  font-size: 1.3rem;
  font-weight: 900;
  color: #edbe38;
  margin: 0 1px;
}

/* Seconds Segment Special Glow */
.seconds-segment {
  background: rgba(0, 151, 215, 0.15);
  border-color: #0097d7;
}

.seconds-number {
  color: #ffffff;
  text-shadow: 0 0 16px rgba(0, 151, 215, 0.8);
  display: inline-block;
  transition: transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.tick-scale {
  transform: scale(1.12);
}

/* Target Date Subtext */
.target-date-subtext {
  font-size: 0.72rem;
  color: #ffffff;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ==========================================================================
   URGENCY & COMPLETION VISUAL MODES
   ========================================================================== */

/* 1 Hour Urgency Mode (JCI Gold #EDBE38 Accent) */
.urgent-card {
  border-color: #edbe38 !important;
  box-shadow: 0 20px 45px rgba(10, 15, 41, 0.65),
    0 0 30px rgba(237, 190, 56, 0.3) !important;

  .border-glow-line {
    background: linear-gradient(
      90deg,
      transparent 0%,
      #edbe38 50%,
      transparent 100%
    ) !important;
  }

  .timer-eyebrow {
    color: #edbe38 !important;
  }

  .timer-icon-badge {
    background: rgba(237, 190, 56, 0.18) !important;
    border-color: #edbe38 !important;
  }

  .segment-colon {
    color: #edbe38 !important;
  }
}

/* 10 Minutes Critical Mode (JCI Gold Glow & Pulse) */
.critical-card {
  border-color: #edbe38 !important;
  animation: card-critical-pulse 2s infinite ease-in-out;
}

@keyframes card-critical-pulse {
  0%,
  100% {
    box-shadow: 0 20px 45px rgba(10, 15, 41, 0.65),
      0 0 25px rgba(237, 190, 56, 0.25);
  }
  50% {
    box-shadow: 0 20px 45px rgba(10, 15, 41, 0.65),
      0 0 42px rgba(237, 190, 56, 0.5);
  }
}

/* 10 Seconds Final Mode (JCI Gold Seconds Accent) */
.final-card {
  border-color: #edbe38 !important;

  .border-glow-line {
    background: linear-gradient(
      90deg,
      transparent 0%,
      #edbe38 50%,
      transparent 100%
    ) !important;
  }

  .timer-eyebrow {
    color: #edbe38 !important;
  }

  .seconds-segment {
    background: rgba(237, 190, 56, 0.2) !important;
    border-color: #edbe38 !important;
  }

  .seconds-number {
    color: #edbe38 !important;
    text-shadow: 0 0 16px rgba(237, 190, 56, 0.8) !important;
  }
}

.final-pulse {
  animation: seconds-heartbeat 1s infinite cubic-bezier(0.215, 0.61, 0.355, 1);
}

@keyframes seconds-heartbeat {
  0% {
    transform: scale(1);
  }
  14% {
    transform: scale(1.06);
  }
  28% {
    transform: scale(1);
  }
  42% {
    transform: scale(1.06);
  }
  70% {
    transform: scale(1);
  }
}

/* Completed Card Styling (JCI Seafoam #56BDA3) */
.completion-card {
  border-color: #56bda3 !important;
  box-shadow: 0 20px 45px rgba(10, 15, 41, 0.65),
    0 0 30px rgba(86, 189, 163, 0.3) !important;

  .border-glow-line {
    background: linear-gradient(
      90deg,
      transparent 0%,
      #56bda3 50%,
      transparent 100%
    ) !important;
  }
}

.completion-icon-wrapper {
  animation: completion-pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes completion-pop {
  0% {
    transform: scale(0.4);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.completion-title {
  font-size: 1.05rem;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: 0.5px;
}

.completion-subtitle {
  font-size: 0.72rem;
  font-weight: 800;
  color: #56bda3;
  letter-spacing: 1px;
}

/* ==========================================================================
   RESPONSIVE DESIGN ADJUSTMENTS
   ========================================================================== */
@media (max-width: 600px) {
  .floating-countdown-timer-container {
    bottom: 16px;
    right: 16px;
  }

  .countdown-card-expanded {
    min-width: 260px;
    max-width: 300px;
    padding: 12px !important;
  }

  .timer-segment-box {
    min-width: 44px;
    padding: 6px 8px;
  }

  .segment-number {
    font-size: 1.25rem;
  }
}
</style>

